import React, { useEffect, useMemo, useState } from 'react';
import { GoBackButton } from '@shared/ui/atoms/buttons/GoBackButton';
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import { selectCurrentUser } from "@shared/store/auth.duck";
import { isCompanyAdminUser } from "@shared/utils/AccessControl";
import AccessRequired from "@features/settings/pages/components/AccessRequired";
import { HeadlineLarge } from '@shared/ui/atoms/fonts/Headline';
import { getAllLineReports, LineReportLine, toExcelDownload } from '@features/reporting/services/ReportingService';
import { failureNotification } from "@shared/store/notification.duck";
import { LoadingSpinner } from '@shared/ui/atoms/LoadingSpinner';
import { useTranslation } from "react-i18next";
import { TableContents } from '@shared/ui/atoms/TableStructure';
import Colours from '@shared/ui/atoms/Colours';
import { OutlineButton } from '@shared/ui/atoms/buttons/OutlineButton';
import { BodyLarge, BodyRegular } from '@shared/ui/atoms/fonts/Body';
import { tablet } from '@shared/ui/atoms/MediaQueries';
import { BiggerThanMobileOnly, MobileOnly } from '@shared/ui/atoms/DeviceSpecific';


export function LineReportsReportingPage(props: Props) {
  const [loading, setLoading] = useState(false);
  const currentUser = useSelector(selectCurrentUser);
  const [lineReports, setLineReports] = useState<LineReportLine[]>([]);
  const canViewReport = useMemo(() => isCompanyAdminUser(currentUser), [currentUser]);
  const dispatch = useDispatch();
  const {t} = useTranslation();

  useEffect(() => {
    if (canViewReport) {
      if (!isCompanyAdminUser(currentUser)) {
        dispatch(failureNotification(`You must be a company admin`));
        return;
      }

      setLoading(true)
      getAllLineReports()
        .then(lineReports => setLineReports(lineReports))
        .catch(() => dispatch(failureNotification("Failed to get report data")))
        .finally(() => setLoading(false));
    }
  }, [canViewReport, dispatch, currentUser]);

  const exportAsExcel = () => {
    toExcelDownload(lineReports, 'line-reports');
  }

  return (
    <ReportingContainer>
      <GoBackButton url={"/manage/reporting"} />
      {loading && <LoadingSpinner fullScreen={true} />}
      <div>
        {canViewReport ? <>
          <ControlButtons>
            <HeadlineLarge style={{marginBottom: 24}}>{t('reports-page.line-reports-title')}</HeadlineLarge>
            <OutlineButton text={"button.download-excel"} click={exportAsExcel} disabled={loading || lineReports.length === 0} />
          </ControlButtons>
          <MobileOnly>
            <HeaderRow>
              <BodyLarge weight={600}>Manager</BodyLarge>
              <BodyLarge weight={600}>Line report</BodyLarge>
            </HeaderRow>
            <MobileContentRows>
              {lineReports.map((lineReportLine: LineReportLine, key: number) => (
                <MobileRow key={key}>
                  <BodyRegular style={{marginBottom: -8}}>{`${lineReportLine.managerFirstName} ${lineReportLine.managerLastName}`}</BodyRegular>
                  <BodyRegular style={{textAlign: 'right', marginTop: -8}}>{`${lineReportLine.reporteeFirstName} ${lineReportLine.reporteeLastName}`}</BodyRegular>
                </MobileRow>
              ))}
            </MobileContentRows>
          </MobileOnly>
          <BiggerThanMobileOnly>
            <TableContainer>
              <table>
                <thead>
                <th className="table__userCell" style={{marginLeft: 'auto'}}>
                  <BodyLarge weight={600}>Manager</BodyLarge>
                </th>
                <th className="table__userCell">
                  <BodyLarge weight={600}>Line report</BodyLarge>
                </th>
                <th className="table__userCell">
                </th>
                </thead>
                <tbody>
                {lineReports.map((lineReportLine: LineReportLine, key: number) => (
                  <tr key={key}>
                    <LineReportRow lineReport={lineReportLine} />
                  </tr>
                ))}
                </tbody>
              </table>
            </TableContainer>
          </BiggerThanMobileOnly>
        </> : <>
          <AccessRequired visible={!canViewReport} teamAdminsRequired={false} companyAdminsRequired={true} />
        </>}
      </div>
    </ReportingContainer>
  )
}

function LineReportRow(props: {lineReport: LineReportLine}) {
  const {lineReport} = props;
  return <>
    <td className="table__userCell" style={{marginLeft: 'auto'}}>
      <TableContents colour={Colours.black}>
        {`${lineReport.managerFirstName} ${lineReport.managerLastName}`}
      </TableContents>
    </td>
    <td className="table__userCell">
      <TableContents colour={Colours.black}>
        {`${lineReport.reporteeFirstName} ${lineReport.reporteeLastName}`}
      </TableContents>
    </td>
    <td className="table__userCell">
      <TableContents>
      </TableContents>
    </td>
  </>
}

interface Props {
}

const ReportingContainer = styled.div`
  max-width: 100%;
  min-height: 100vh;
  background-color: white;
  padding-top: 24px;
  @media (${tablet}) {
    padding: 24px;
  }
`

const ControlButtons = styled.div`
  display: flex;
  flex-direction: column;
  @media (${tablet}) {
    justify-content: space-between;
    flex-direction: row;
  }
`

const TableContainer = styled.div`
  max-width: 100%;
  overflow-x: auto;
`

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  border-bottom: 1px solid ${Colours.black};
`

const MobileContentRows = styled.div`
`

const MobileRow = styled.div`
  padding: 2px 4px;
  margin: 8px 0;
  &:nth-child(odd) {
    background-color: ${Colours.veryLightGrey};
  }
`
